import {defineStore} from "pinia";
import axios from "axios";
import type {
    CheckActiveSessionResponse,
    GenerateQrCodeResponse, GenerateTwoFactorCodeResponse,
    TwoFactorProvider,
    VerifyTwoFactorCodeResponse
} from "~/types/TwoFactor";

export const useTwoFactorStore = defineStore('two-factor', {
    state: () => ({
        twoFactorProviders: [] as Array<TwoFactorProvider>,
        _fetchingTwoFactorProviders: null as Promise<any> | null,
        userTwoFactorProvider: null as TwoFactorProvider | null,
        _fetchingUserTwoFactorProvider: null as Promise<any> | null,
        isTwoFactorAuthRequired: false,
    }),
    actions: {
        async generateQrCode(accessToken: string, token: string | undefined): Promise<GenerateQrCodeResponse | null> {
            try {
                const config = {
                    headers: {
                        Authorization: `Bearer ${accessToken}`
                    }
                };

                const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/v1/auth/two-factor/generate-qr-code?token=${token}`, config);
                if (response.data.data) {
                    return response.data.data as GenerateQrCodeResponse;
                }
            } catch (error) {
                return null;
            }
            return null;
        },
        async verifyTwoFactorCode(
            accessToken: string,
            code: string,
            providerCode: string,
            userAgent: string,
            userIp: string,
            token: string | undefined,
            secret?: string | null
        ): Promise<VerifyTwoFactorCodeResponse | null> {
            try {
                const config = {
                    headers: {
                        Authorization: `Bearer ${accessToken}`
                    }
                };

                const response = await axios.post(`${import.meta.env.VITE_API_BASE_URL}/api/v1/auth/two-factor/verify-code`, {
                    token,
                    'two_factor_code': code,
                    secret,
                    user_agent: userAgent,
                    user_ip: userIp,
                    provider_code: providerCode
                }, config);
                return response.data.data as VerifyTwoFactorCodeResponse;
            } catch (error) {
                return null;
            }
        },
        async checkActiveSession(accessToken: string, userAgent: string, userIp: string): Promise<CheckActiveSessionResponse|null> {
            try {
                const config = {
                    headers: {
                        Authorization: `Bearer ${accessToken}`
                    }
                };

                const response = await axios.post(`${import.meta.env.VITE_API_BASE_URL}/api/v1/auth/two-factor/check-active-session`, {
                    user_agent: userAgent,
                    user_ip: userIp
                }, config);
                const data = response.data.data as CheckActiveSessionResponse;
                this.isTwoFactorAuthRequired = data.is_two_factor_auth_required; // Update the state variable
                return data;
            } catch (error) {
                return null;
            }
        },
        async removeTwoFactorAuth(accessToken: string, recoveryKey: string, providerCode: string, token: string | undefined): Promise<boolean> {
            try {
                const config = {
                    headers: {
                        Authorization: `Bearer ${accessToken}`
                    }
                };

                const response = await axios.post(`${import.meta.env.VITE_API_BASE_URL}/api/v1/auth/two-factor/remove`, {
                    token,
                    recovery_key: recoveryKey,
                    provider_code: providerCode
                }, config);
                return response.data.data.is_removed as boolean;
            } catch (error) {
                return false;
            }
        },
        async fetchTwoFactorProviders(): Promise<Array<TwoFactorProvider>> {
            if (this._fetchingTwoFactorProviders) {
                return this._fetchingTwoFactorProviders;
            }

            this._fetchingTwoFactorProviders = axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/v1/two-factor-auth-providers`)
                .then(response => {
                    this.twoFactorProviders = response.data.data as Array<TwoFactorProvider>;
                    return this.twoFactorProviders;
                })
                .finally(() => {
                    this._fetchingTwoFactorProviders = null;
                });

            return this._fetchingTwoFactorProviders;
        },
        async getTwoFactorProviders(): Promise<Array<TwoFactorProvider>> {
            if (this.twoFactorProviders.length === 0) {
                await this.fetchTwoFactorProviders();
            }

            return this.twoFactorProviders;
        },
        async generateTwoFactorCode(accessToken: string, userAgent: string, userIp: string, providerCode: string, token: string | undefined): Promise<GenerateTwoFactorCodeResponse | null> {
            try {
                const config = {
                    headers: {
                        Authorization: `Bearer ${accessToken}`
                    }
                };

                const response = await axios.post(`${import.meta.env.VITE_API_BASE_URL}/api/v1/auth/two-factor/generate-code`, {
                    token,
                    user_agent: userAgent,
                    provider_code: providerCode,
                    user_ip: userIp
                }, config);
                return response.data.data as GenerateTwoFactorCodeResponse;
            } catch (error) {
                return null;
            }
        },
        async fetchUserTwoFactorProvider(accessToken: string, token: string | undefined): Promise<TwoFactorProvider | null> {
            if (this._fetchingUserTwoFactorProvider) {
                return this._fetchingUserTwoFactorProvider;
            }

            this._fetchingUserTwoFactorProvider = axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/v1/two-factor-auth-providers/user?token=${token}`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`
                }
            })
                .then(response => {
                    this.userTwoFactorProvider = response.data.data as TwoFactorProvider;
                    return this.userTwoFactorProvider;
                })
                .finally(() => {
                    this._fetchingUserTwoFactorProvider = null;
                });

            return this._fetchingUserTwoFactorProvider;
        },
    },
    persist: {
        storage: persistedState.sessionStorage,
        paths: [
            'twoFactorProviders',
            'userTwoFactorProvider',
            'isTwoFactorAuthRequired'
        ],
        key: 'twoFactorStore',
    }
});
